
import { defineComponent, reactive, toRefs, onUnmounted } from "vue";
import VueFeather from "vue-feather";
import { fetchEvents, fetchIncidents, fetchAggregators } from "@/helpers/api";
import { Aggregator } from "@/interfaces/Aggregator";
import { Event } from "@/interfaces/Event";
import { Incident } from "@/interfaces/Incident";
import { formatTime, formatTimeSince } from "@/helpers/time";
import { socket } from "@/helpers/websocket";
import { Temporal } from "@js-temporal/polyfill";

export default defineComponent({
  name: "OverviewPage",
  components: {
    VueFeather,
  },
  setup() {
    const state = reactive({
      aggregators: [] as Aggregator[],
      events: [] as Event[],
      incidents: [] as Incident[],
      refresh: false as boolean,
    });

    const getAggregators = async () => {
      const aggregators = await fetchAggregators();
      state.aggregators = sortAggregators(
        aggregators.map((aggregator) => ({
          ...aggregator,
          online: aggregator.Status == "online",
        }))
      );
    };

    const getIncidents = async () => {
      const incidentParams = new URLSearchParams();
      incidentParams.set("Active", "yes");
      const incidents = await fetchIncidents(incidentParams);
      state.incidents = incidents;
    };

    const getEvents = async () => {
      const eventParams = new URLSearchParams();
      eventParams.set("Limit", "10");
      state.events = await fetchEvents(eventParams);
    };

    const handleNewEvent = (event: Event) => {
      state.events.unshift(event);
      if (state.events.length > 10) state.events.pop();
    };

    const flattenTags = (agg: Aggregator) => `${agg.Tags.Aggregator}${agg.Tags.Customer}${agg.Tags.Site}`;

    const handleNewAggregatorStatus = (aggregator: Aggregator) => {
      state.aggregators = sortAggregators(
        state.aggregators
          .filter((sa) => flattenTags(sa) !== flattenTags(aggregator))
          .concat({ ...aggregator, online: aggregator.Status == "online" })
      );
    };

    const sortAggregators = (aggregators: Aggregator[]) =>
      aggregators.sort((a, b) =>
        a.online === b.online ? Temporal.Instant.compare(b.Time, a.Time) : a.online ? -1 : 1
      );

    socket.bind("new_event", handleNewEvent);
    socket.bind("aggregator_status", handleNewAggregatorStatus);

    const interval = setInterval(() => {
      state.refresh = !state.refresh;
    }, 60000);

    onUnmounted(() => {
      clearInterval(interval);
    });

    getAggregators();
    getIncidents();
    getEvents();

    return {
      ...toRefs(state),
      formatTimeSince,
      formatTime,
    };
  },
});
